import { noop, uniqBy } from 'lodash-es';
import { useCallback, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { list as listApi, read as readApi } from 'services/api/notice';

const limit = 10;

function useNoticeList() {
  const [list, setList] = useState<Notice.Item[]>([]);
  const [listType, setListType] = useState<Notice.FilterType>(0);

  const [hasMore, setHasMore] = useState(true);

  const pageRef = useRef(1);

  const loadMore = useCallback(() => {
    return listApi({
      page: pageRef.current,
      limit,
      type: listType,
    }).then((res) => {
      if (res.length < limit) {
        setHasMore(false);
      }

      if (res.length > 0) {
        setList((pre) => uniqBy([...pre, ...res], 'id'));
      }

      pageRef.current += 1;
    });
  }, [listType]);

  const refresh = useCallback((listType: Notice.FilterType) => {
    pageRef.current = 1;
    unstable_batchedUpdates(() => {
      setList([]);
      setHasMore(true);
      setListType(listType);
    });
  }, []);

  const unShiftList = useCallback((list: Notice.Item[]) => {
    setList((pre) => uniqBy([...list, ...pre], 'id'));
  }, []);

  const syncList = useCallback(() => {
    return listApi({
      page: 1,
      limit,
      type: listType,
    })
      .then(unShiftList)
      .catch(noop);
  }, [listType, unShiftList]);

  const read = useCallback((notice: Notice.Item) => {
    const { id, isRead } = notice;

    if (isRead) {
      return;
    }

    readApi(id)
      .then(() => {
        setList((list) => {
          return list.map((notice) => {
            if (notice.id !== id) {
              return notice;
            }

            return {
              ...notice,
              isRead: true,
            };
          });
        });
      })
      .catch(noop);
  }, []);

  return {
    list,
    setList,
    listType,
    refresh,
    hasMore,
    setHasMore,
    loadMore,
    unShiftList,
    syncList,
    read,
  };
}

export default useNoticeList;
